import {
    AggregationMode,
    Dictionary,
    IValueViewModel,
    NameBuilderConfiguration
} from '../../models';
import { ValueTypes } from '../../common/ValueTypes';
import { CellType } from './CellType';
import { BuiltRow } from './BuiltRow';

/**
 * Buils a list of headings for a list of series.
 * @param configuration The widget configuration.
 * @param series The list of series to display.
 * @param viewModels The dictionary of view models.
 * @returns The list of headings to display.
 */
export function getHeadings(
    configuration: {
        nameConfiguration: NameBuilderConfiguration;
        aggregationColumnMode?: AggregationMode;
        dashboardGuid?: string;
    },
    series: { guid: string; value: string; }[],
    viewModels: Dictionary<IValueViewModel>
): BuiltRow[] {
    const headings: BuiltRow[] = [];
    const nc = configuration.nameConfiguration;
    if (nc.hasGroupName) {
        headings.push({
            cells: series.map(s => {
                const vm = viewModels[s.guid];
                const group = vm && vm.groupList && vm.groupList.length ? vm.groupList[0] : null;
                return {
                    cellType: CellType.Group,
                    group,
                    id: s.guid + s.value,
                    value: group?.uniqueName
                };
            })
        });
    }
    if (nc.hasName) {
        headings.push({
            cells: series.map(s => {
                const vm = viewModels[s.guid];
                return {
                    cellType: vm.objectType === 1 ? CellType.Identifier : CellType.Tag,
                    viewModel: vm,
                    id: s.guid + s.value,
                    value: vm?.name
                };
            })
        });
    }
    if (nc.hasShortComment) {
        headings.push({
            cells: series.map(s => {
                const vm = viewModels[s.guid];
                return {
                    cellType: CellType.String,
                    value: vm.shortComment,
                    id: s.guid + s.value
                };
            })
        });
    }
    if (nc.hasUnit) {
        headings.push({
            cells: series.map(s => {
                const vm = viewModels[s.guid];
                return {
                    cellType: CellType.String,
                    value: vm.unitName,
                    id: s.guid + s.value
                };
            })
        });
    }
    if (nc.hasValueType) {
        headings.push({
            cells: series.map(s => {
                const vm = viewModels[s.guid];
                return {
                    cellType: CellType.String,
                    value: ValueTypes.getValueType(s.value),
                    id: s.guid + s.value
                };
            })
        });
    }
    if (configuration.aggregationColumnMode !== AggregationMode.None && headings.length) {
        headings[0].cells.push({
            cellType: CellType.String,
            id: 'aggregationMode',
            value: AggregationMode[configuration.aggregationColumnMode]
        });
        for (let i = 1; i < headings.length; i++) {
            headings[i].cells.push({ cellType: CellType.String, value: '', id: 'aggregationMode' });
        }
    }
    return headings;
}
